import { useStrings } from "@1js/localization-components";
import {
  Button,
  makeStyles,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import * as React from "react";
import { useFlightContext } from "../../hooks/useFlightContext";
import { fetchAllPrompts } from "../../services/prompt-client.web";
import useTelemetry from "../../utils/Telemetry/useTelemetry";
import { AllPromptsLibrary } from "../prompts/allPromptsLibrary/AllPromptsLibrary.web";
import usePrompts from "../prompts/usePrompts.web";
import {
  PromptsHeading,
  ViewMorePrompts,
} from "./CapabilitiesPageContent.web.strings";

export type CapabilitiesPageContentProps = {
  locale: string;
  serviceHost: string;
};

const useStyles = makeStyles({
  heading: {
    textAlign: "center",
    fontSize: "4rem",
    fontWeight: tokens.fontWeightRegular,
    fontFamily: "Segoe UI Variable",
    lineHeight: "normal",
    ...shorthands.margin("0px"),

    "@media (max-width: 540px)": {
      fontSize: "1.5rem",
      fontWeight: "600",
      fontFamily: "Segoe UI",
      lineHeight: "2rem",
      fontStyle: "normal",
    },
  },

  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    ...shorthands.gap("3rem"),
    "@media (max-width: 540px)": {
      ...shorthands.gap("2rem"),
    },
  },

  promptLib: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },

  allPrompts: {
    ...shorthands.padding("0px"),
    "@media (max-width: 1422px)": {
      maxWidth: "unset",
    },

    "@media (max-width: 953px) and (min-width: 541px)": {
      maxWidth: "unset",
      ...shorthands.padding("0px"),
      width: "100%",
    },

    "@media (max-width: 540px)": {
      width: "100%",
    },
  },

  viewMoreBtnWrapper: {
    display: "flex",
    justifyContent: "center",

    // This is hack because AllPrompts component has a 48px padding bottom. This
    // would give us a total 20 pixel spacing to match figma.
    marginTop: "-28px",

    "@media (max-width: 540px)": {
      marginTop: "-36px",
    },
  },

  viewMoreBtn: {
    color: tokens.colorBrandForeground1,
  },
});

const { origin, pathname } = window.location;
const allLink = `${origin}${pathname}/all`;

export const CapabilitiesPageContent: React.FC<
  CapabilitiesPageContentProps
> = ({ serviceHost, locale }) => {
  const telemetry = useTelemetry();
  const localizations = useStrings({
    ViewMorePrompts,
    PromptsHeading,
  });
  const styles = useStyles();
  const { isFlightActive, setFlightState } = useFlightContext();
  const { state, dispatch } = usePrompts();

  const fetchPromptsAsync = React.useCallback(async () => {
    if (!state.init) {
      try {
        const startTimer = Date.now();
        dispatch({ type: "REQUEST_ALL_PROMPTS_INIT" });
        const [prompts, is3SServiceEnabled, isBookmarksEnabled] =
          await fetchAllPrompts(
            serviceHost,
            locale,
            isFlightActive("is3SServiceEnabled")
          );
        dispatch({
          type: "REQUEST_ALL_PROMPTS_SUCCEED",
          payload: { prompts: prompts.slice(0, 8) },
        });
        setFlightState("is3SServiceEnabled", is3SServiceEnabled);
        setFlightState("isBookmarksEnabled", isBookmarksEnabled);
        telemetry.sendResultsRenderedEvent(
          Date.now() - startTimer,
          is3SServiceEnabled,
          prompts
        );
      } catch (error) {
        console.error("Error fetching more prompts:", error);
        dispatch({ type: "REQUEST_ALL_PROMPTS_FAILED" });
      }
    }
    // disable exhaustive deps because we don't want to refetch prompts when promptSettings changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.init]);

  React.useEffect(() => {
    fetchPromptsAsync();
  }, [fetchPromptsAsync]);

  return (
    <div className={styles.main}>
      <h2 className={styles.heading}>{localizations.PromptsHeading()}</h2>
      <div className={styles.promptLib}>
        <AllPromptsLibrary
          status={state.status}
          isShowFilters={false}
          isShowHasMore={false}
          prompts={state.allPrompts}
          serviceHost={serviceHost}
          locale={locale}
          skeletonCount={8}
          className={styles.allPrompts}
        />
        <div className={styles.viewMoreBtnWrapper}>
          <Button
            className={styles.viewMoreBtn}
            size="large"
            appearance="subtle"
            as="a"
            href={allLink}
          >
            {localizations.ViewMorePrompts()}
          </Button>
        </div>
      </div>
    </div>
  );
};
